import { formatAmount } from "../../include/money";
import { useCallback, useContext, useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { DataContext } from "../../context/DataContext";
import { callPostApi } from "../../function/ApiCallFunction";
import { adminPost } from "../admin/adminApi";
import { clubPost } from "../club/clubApi";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";

const empty = {
  earn_cart_amount: "200",
  redeem_cart_amount: "100",
  point_life: "12",
  max_redeem_percentage: "20",
  max_point_earn: "500",
  earn_point_type: "1",
  incremental_cart_amount: "100",
  earn_point: "10",
  earn_point_percent: "1",
  digital_upi_id: "",
  welcome_point: "50",
  referral_bonus_point: "25",
  point_value: "1",
  loyalty_enable: "Yes",
};

function sourceNote(source, clubTitle) {
  if (source === "club") return `These settings come from the club${clubTitle ? ` · ${clubTitle}` : ""}.`;
  if (source === "console") return "These settings come from the console default.";
  return "These settings are for this store.";
}

export default function StoreLoyaltySetting({ source = "store" }) {
  const { loginData, clubData, setClubData } = useContext(DataContext);
  const storeId = loginData?.store_info?.store_id;
  const clubId = clubData?.club_info?.club_id;
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [canEdit, setCanEdit] = useState(source === "admin");
  const [origin, setOrigin] = useState(source);
  const [clubTitle, setClubTitle] = useState("");
  const [storeCanEdit, setStoreCanEdit] = useState(clubData?.club_info?.store_can_edit_loyalty || "1");
  const ready = source === "admin" || (source === "club" ? Boolean(clubId) : Boolean(storeId));

  const load = useCallback(async () => {
    if (!ready) return;
    let result;
    if (source === "admin") result = await adminPost("LOYALTY_DEFAULT_GET");
    else if (source === "club") result = await clubPost("LOYALTY_GET", { club_id: clubId });
    else result = await callPostApi("store-detail", { mod: "STORE_LOYALTY_DETAIL", data_arr: { store_id: storeId } });
    if (result.status !== "200") {
      toast.error(result.status_message || result.data?.error || "Could not load loyalty settings");
      return;
    }
    const settings = result.data?.settings || result.data?.loyaltyDetail || {};
    setForm({ ...empty, ...settings });
    setCanEdit(source === "admin" ? true : result.data?.can_edit !== false);
    setOrigin(result.data?.source || source);
    setClubTitle(result.data?.club_title || "");
    if (result.data?.store_can_edit_loyalty) setStoreCanEdit(result.data.store_can_edit_loyalty);
  }, [ready, source, storeId, clubId]);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      let result;
      const payload = { ...form, incremtCartAmount: form.incremental_cart_amount };
      if (source === "admin") result = await adminPost("LOYALTY_DEFAULT_SAVE", payload);
      else if (source === "club") result = await clubPost("LOYALTY_SAVE", { club_id: clubId, store_can_edit_loyalty: storeCanEdit, ...payload });
      else result = await callPostApi("store-update", { mod: "UPDATE_LOYALTY", data_arr: { store_id: storeId, ...payload } });
      if (result.status === "200") {
        toast.success(result.data?.message || "Loyalty settings saved");
        if (source === "club" && clubData?.club_info) {
          const updated = { ...clubData, club_info: { ...clubData.club_info, store_can_edit_loyalty: storeCanEdit } };
          setClubData(updated);
          sessionStorage.setItem("clubData", JSON.stringify(updated));
        }
      } else toast.info(result.status_message || result.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, helper, extra = {}) => (
    <TextField
      fullWidth
      label={label}
      value={form[key] ?? ""}
      onChange={(event) => setField(key, event.target.value)}
      helperText={helper}
      disabled={!canEdit}
      type={extra.type || "number"}
      inputProps={extra.type === "text" ? undefined : { min: 0, step: extra.step || "1" }}
    />
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 920 }}>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: 20, md: 22 }, fontWeight: 750, color: consoleColors.ink, letterSpacing: -0.2 }}>Loyalty settings</Typography>
          <Typography sx={{ fontSize: 14, color: consoleColors.muted, mt: 0.5, lineHeight: 1.5 }}>
            {source === "admin"
              ? "Default for every club and store. A club or store can change this only when that is allowed."
              : source === "club"
                ? (canEdit ? "These settings apply to stores in this club that cannot edit loyalty themselves. Member stores can spend each other's points, except stores with the same business type." : "The console has locked loyalty settings for this club.")
                : (canEdit ? sourceNote(origin, clubTitle) : sourceNote(origin === "store" ? "console" : origin, clubTitle))}
          </Typography>
        </Box>

        {source === "club" && (
          <TextField
            select
            label="Stores in this club can change loyalty settings"
            value={storeCanEdit}
            onChange={(event) => setStoreCanEdit(event.target.value)}
            helperText="If no, member stores use the club or console settings."
          >
            <MenuItem value="1">Yes</MenuItem>
            <MenuItem value="0">No</MenuItem>
          </TextField>
        )}

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: consoleColors.muted, letterSpacing: 0.4, mb: 1.2 }}>EARN</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
            {field("earn_cart_amount", "Minimum order to earn a point", "First point is earned from this bill amount.")}
            {field("max_point_earn", "Maximum points a customer can hold", "Cap on points in the customer wallet.")}
            {field("point_life", "Point validity (months)", "Points expire after this many months.")}
            <TextField select label="How points are earned" value={form.earn_point_type} onChange={(event) => setField("earn_point_type", event.target.value)} disabled={!canEdit}>
              <MenuItem value="1">Fixed points per extra amount</MenuItem>
              <MenuItem value="2">Percentage of the bill</MenuItem>
            </TextField>
            {form.earn_point_type === "2" ? (
              field("earn_point_percent", "Earn percentage", "Percent of the bill given as points.")
            ) : (
              <>
                {field("incremental_cart_amount", "Extra amount for more points", "Each time the bill grows by this amount.")}
                {field("earn_point", "Points for that extra amount", "Points given for each step above.")}
              </>
            )}
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: consoleColors.muted, letterSpacing: 0.4, mb: 1.2 }}>REDEEM</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
            {field("redeem_cart_amount", "Minimum order to use points", "Points can be used from this bill amount.")}
            {field("max_redeem_percentage", "Maximum redeem percent", "Share of the bill that points can cover.")}
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: consoleColors.muted, letterSpacing: 0.4, mb: 1.2 }}>BONUS</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
            {field("welcome_point", "Welcome points", "Given when a customer joins loyalty.")}
            {field("referral_bonus_point", "Referral bonus points", "Given on the referral’s first purchase.")}
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: consoleColors.muted, letterSpacing: 0.4, mb: 1.2 }}>PAYMENT</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
            {field("digital_upi_id", "Digital UPI ID", "Used when a customer pays this store.", { type: "text" })}
            <Box sx={{ display: "flex", alignItems: "center", px: { md: 0.5 } }}>
              <Typography sx={{ fontSize: 16, fontWeight: 750, color: consoleColors.ink }}>1 loyalty point = ₹{formatAmount(form.point_value || 1)}</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" sx={{ ...primaryButtonSx, px: 3, py: 1.1, fontSize: 15 }} disabled={saving || (source !== "club" && !canEdit)} onClick={save}>
            {saving ? "Saving..." : source === "club" && !canEdit ? "Save store option" : "Save settings"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
